<template>
  <section class="sl-hero" ref="sectionRef" aria-label="Servicios de limpieza">
    <!-- Imagen de fondo -->
    <div class="sl-hero__media">
      <img
        class="sl-hero__img"
        :src="imgSrc"
        :alt="imgAlt"
        ref="imgRef"
        fetchpriority="high"
      />
    </div>

    <!-- Banda verde -->
    <div class="sl-hero__band" ref="bandRef">
      <h1 class="sl-hero__title" ref="titleRef">
        {{ title }}
      </h1>
    </div>

    <!-- Cinta vertical (opcional) -->
    <a
      v-if="showRibbon"
      class="sl-hero__ribbon"
      :href="ribbonHref"
      target="_blank"
      rel="noopener"
    >
      {{ ribbonText }}
      <span class="sl-hero__ribbon-icon" aria-hidden="true">▢</span>
    </a>
  </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

export default {
  name: 'ServicioLimpiezaHero',

  props: {
    title: { type: String, default: 'Servicios de Limpieza' },
    imgSrc: {
      type: String,
      default: () => require('@/assets/hero-principal.jpg')
    },
    imgAlt: { type: String, default: 'Sala limpia y ordenada' },
    showRibbon: { type: Boolean, default: true },
    ribbonText: { type: String, default: 'GET A FREE ESTIMATE' },
    ribbonHref: { type: String, default: '#' }
  },

  mounted() {
    gsap.registerPlugin(ScrollTrigger)

    const section = this.$refs.sectionRef
    const band    = this.$refs.bandRef
    const titleEl = this.$refs.titleRef
    const img     = this.$refs.imgRef

    if (!section || !band || !titleEl || !img) return

    // Animar letras
    const original = this.title || ''
    titleEl.textContent = ''
    const frag = document.createDocumentFragment()
    const spans = []

    for (const ch of original) {
      const span = document.createElement('span')
      span.className = 'char'
      span.textContent = ch === ' ' ? '\u00A0' : ch
      frag.appendChild(span)
      spans.push(span)
    }
    titleEl.appendChild(frag)

    gsap.set(band, { clipPath: 'inset(0 100% 0 0)' })
    gsap.set(spans, { opacity: 0, y: 6 })
    gsap.set(img, { scale: 1.06, filter: 'brightness(0.9) contrast(1.05)' })

    const tl = gsap.timeline({
      scrollTrigger: { trigger: section, start: 'top 85%', once: true }
    })

    tl.to(band, { clipPath: 'inset(0 0% 0 0)', duration: 0.9, ease: 'power3.out' })
      .to(spans, {
        opacity: 1,
        y: 0,
        duration: 0.02,
        stagger: { each: 0.04, from: 0 },
        ease: 'power1.out'
      }, '-=0.4')
      .to(img, {
        scale: 1,
        filter: 'brightness(1) contrast(1)',
        duration: 1.1,
        ease: 'power2.out'
      }, '-=0.5')

    gsap.to(img, {
      yPercent: 8,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.6
      }
    })
  },

  beforeUnmount() {
    ScrollTrigger.getAll().forEach(t => t.kill())
  }
}
</script>

<style scoped>
:root,
.sl-hero {
  --sl-green: #dbd753;
  --sl-white: #ffffff;
  --sl-bg: #ffffff;
}

.sl-hero {
  position: relative;
  width: 100%;
  min-height: 90vh;
  background: var(--sl-bg);
  overflow: hidden;
  display: grid;
  place-items: center;
}

/* Imagen de fondo */
.sl-hero__media {
  position: absolute;
  inset: 0;
  overflow: hidden;
}
.sl-hero__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  will-change: transform, filter;
}

/* Banda verde centrada */
.sl-hero__band {
  position: relative;
  z-index: 2;
  width: min(1100px, 92vw);
  margin: 0 auto;
  background: var(--sl-green);
  padding: clamp(14px, 2.5vw, 22px) clamp(24px, 4vw, 36px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  text-align: center; /* centramos texto */
}

/* Título */
.sl-hero__title {
  margin: 0;
  color: var(--sl-white);
  font-family: 'Montserrat', system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1.2;
  font-size: clamp(28px, 5vw, 64px);
}
.char { display: inline-block; }

/* Cinta vertical */
.sl-hero__ribbon {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: right center;
  background: var(--sl-green);
  color: var(--sl-white);
  text-decoration: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  padding: 12px 18px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  z-index: 3;
  border-radius: 2px 2px 0 0;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}
.sl-hero__ribbon:hover { filter: brightness(0.95); }
.sl-hero__ribbon-icon { font-size: 12px; opacity: 0.9; }

@media (max-width: 640px) {
  .sl-hero { min-height: 56vh; }
  .sl-hero__ribbon { display: none; }
}
</style>
